/**
 * Fase 8 — painel "Perguntar à IA": conversa em streaming com o contexto do período/cliente/contas
 * da tela (Campanhas, Relatórios ou a tela do assistente), markdown seguro, copiar, parar geração,
 * "análise aprofundada" (modelo deep) e créditos restantes no cabeçalho. A conversa fica no histórico.
 */
import type { AiContextInput, AiMessageDto } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Copy, History, Send, Sparkles, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditsBadge } from "@/components/ai/credits-badge";
import { Markdown } from "@/components/ai/markdown";
import { useAiStream } from "@/components/ai/use-ai-stream";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { aiApi, aiKey, aiQueries, streamMessage } from "@/lib/ai-api";
import { cn } from "@/lib/utils";

interface LocalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  interrupted?: boolean;
  credits?: number | null;
}

const SUGGESTIONS = [
  "Como está o desempenho neste período?",
  "Por que o custo por resultado mudou?",
  "Quais campanhas devo escalar ou pausar?",
  "O que testar na próxima semana?",
];

function CopyButton({ text }: { text: string }) {
  const [ok, setOk] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7"
      aria-label="Copiar resposta"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setOk(true);
          setTimeout(() => setOk(false), 1500);
        });
      }}
    >
      {ok ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
}

export function AiPanel({
  workspaceId,
  context,
  conversationId: initialConversationId,
  title = "Perguntar à IA",
  subtitle,
  onClose,
  variant = "aside",
}: {
  workspaceId: string;
  /** Contexto da nova conversa (ignorado ao continuar uma conversa existente). */
  context: AiContextInput;
  conversationId?: string | null;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  /** `aside` = painel fixo à direita; `inline` = dentro da tela do assistente. */
  variant?: "aside" | "inline";
}) {
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId ?? null);
  /** Só o par em andamento/recém-terminado; o histórico vem do servidor (`existing`). */
  const [local, setLocal] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState("");
  const [deep, setDeep] = useState(false);
  const stream = useAiStream();
  const bottomRef = useRef<HTMLDivElement>(null);
  const existing = useQuery({ ...aiQueries.conversation(workspaceId, conversationId ?? ""), enabled: Boolean(conversationId) });
  const history: LocalMessage[] = (existing.data?.messages ?? []).map((m: AiMessageDto) => ({ id: m.id, role: m.role, content: m.content, interrupted: m.interrupted, credits: m.credits }));
  const known = new Set(history.map((m) => m.id));
  const messages = [...history, ...local.filter((m) => !known.has(m.id))];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, stream.text]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: aiKey(workspaceId) });
  };

  const send = useMutation({
    mutationFn: async (content: string) => {
      let id = conversationId;
      if (!id) {
        const created = await aiApi.createConversation(workspaceId, { context });
        id = created.id;
        setConversationId(id);
      }
      const tempId = `u-${Date.now()}`;
      setLocal([{ id: tempId, role: "user", content }]);
      const result = await stream.start((handlers, signal) =>
        streamMessage(workspaceId, id, { content, deep }, handlers, signal),
      );
      // A resposta (inteira, ou parcial se foi parada) vira mensagem fixa com os ids do servidor;
      // quando o histórico recarrega, as cópias locais somem pelo id.
      setLocal([
        { id: result.userMessageId ?? tempId, role: "user", content },
        ...(result.text || result.done
          ? [
              {
                id: result.done?.messageId ?? `a-${Date.now()}`,
                role: "assistant" as const,
                content: result.text,
                interrupted: result.done?.interrupted ?? true,
                credits: result.done?.credits ?? null,
              },
            ]
          : []),
      ]);
      // Com erro, o aviso continua na tela até a próxima pergunta (o `start` seguinte limpa).
      if (!result.error) stream.reset();
      invalidate();
      return result;
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      invalidate();
    },
  });

  const submit = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || stream.streaming || send.isPending) return;
    setInput("");
    send.mutate(content);
  };

  const body = (
    <>
      <header className="flex items-start gap-2 border-b p-4">
        <Sparkles className="mt-0.5 size-5 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-xs text-muted-foreground">{subtitle ?? "Analisa o que você está vendo e sugere o que testar. A IA sugere; você aprova."}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CreditsBadge workspaceId={workspaceId} />
            <Button asChild variant="ghost" size="sm" className="h-6 px-2 text-xs">
              <Link to="/app/$workspaceId/ia" params={{ workspaceId }} search={{ aba: "conversas" }}>
                <History className="size-3" /> Histórico
              </Link>
            </Button>
          </div>
        </div>
        {onClose ? (
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar painel">
            <X />
          </Button>
        ) : null}
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
        {messages.length === 0 && !stream.streaming ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Pergunte sobre o período e as contas selecionadas. Sugestões:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <Button key={s} variant="outline" size="sm" onClick={() => send.mutate(s)} disabled={send.isPending}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={cn("group flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={cn("max-w-[92%] rounded-xl px-3 py-2", m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted")}>
              {m.role === "user" ? <p className="text-sm whitespace-pre-wrap">{m.content}</p> : <Markdown text={m.content} />}
              {m.role === "assistant" ? (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  {m.interrupted ? <span>interrompida</span> : null}
                  {m.credits ? <span>{m.credits} crédito{m.credits === 1 ? "" : "s"}</span> : null}
                  <CopyButton text={m.content} />
                </div>
              ) : null}
            </div>
          </div>
        ))}
        {stream.streaming ? (
          <div className="flex justify-start">
            <div className="max-w-[92%] rounded-xl bg-muted px-3 py-2">
              {stream.text ? <Markdown text={stream.text} /> : <p className="text-sm text-muted-foreground">Pensando…</p>}
            </div>
          </div>
        ) : null}
        {stream.error ? (
          <p className="text-sm text-destructive" role="alert">
            {stream.error}
          </p>
        ) : null}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="space-y-2 border-t p-3">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escreva sua pergunta…"
          rows={2}
          aria-label="Pergunta para a IA"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
            Análise aprofundada (modelo maior, mais créditos)
          </label>
          <div className="flex-1" />
          {stream.streaming ? (
            <Button type="button" variant="outline" size="sm" onClick={stream.stop}>
              <Square className="size-3.5" /> Parar
            </Button>
          ) : (
            <Button type="submit" size="sm" disabled={!input.trim() || send.isPending}>
              <Send className="size-3.5" /> Enviar
            </Button>
          )}
        </div>
      </form>
    </>
  );

  if (variant === "inline") {
    return <div className="flex h-[70vh] min-h-[420px] flex-col rounded-xl border bg-background">{body}</div>;
  }
  return (
    <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-lg flex-col border-l bg-background shadow-xl" role="dialog" aria-label={title}>
      {body}
    </aside>
  );
}
